import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const AudioPlayer = ({ 
  clipName, 
  fileUrl, 
  autoLoad = true,
  showTitle = true,
  compact = false,
  className = ''
}) => {
  const { t } = useLanguage();
  const audioRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);

  // Load audio metadata when component mounts
  useEffect(() => {
    if (autoLoad && fileUrl && audioRef.current) {
      const audio = audioRef.current;
      
      const handleLoadedMetadata = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
        setIsLoading(false);
      };
      
      const handleCanPlay = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0 && duration === 0) {
          setDuration(audio.duration);
        }
      };
      
      const handleDurationChange = () => {
        if (audio.duration && !isNaN(audio.duration) && isFinite(audio.duration) && audio.duration > 0) {
          setDuration(audio.duration);
        }
      };
      
      const handleError = () => {
        setError('Failed to load audio clip');
        setIsLoading(false);
      };
      
      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        // Also try to get duration during playback if we don't have it
        if ((duration === 0 || isNaN(duration)) && audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };
      
      const handleLoadStart = () => {
        // Force load metadata for WebM files
        if (audio.preload !== 'metadata') {
          audio.preload = 'metadata';
        }
      };
      
      const handleProgress = () => {
        // For WebM files, duration might become available during loading
        if ((duration === 0 || isNaN(duration)) && audio.duration && !isNaN(audio.duration) && isFinite(audio.duration)) {
          setDuration(audio.duration);
        }
      };
      
      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
      };

      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('progress', handleProgress);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('durationchange', handleDurationChange);
      audio.addEventListener('error', handleError);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      
      setIsLoading(true);
      audio.load();
      
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('progress', handleProgress);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('durationchange', handleDurationChange);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [fileUrl, autoLoad]);

  const togglePlayPause = async () => {
    if (!audioRef.current || error) return;
    
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
        
        // Try to get duration if we don't have it yet
        if ((duration === 0 || isNaN(duration)) && audioRef.current.duration && !isNaN(audioRef.current.duration)) {
          setDuration(audioRef.current.duration);
        }
      }
    } catch (err) {
      setError('Failed to play audio');
    }
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || duration === 0) return;
    
    const progressBar = e.currentTarget;
    const clickX = e.nativeEvent.offsetX;
    const width = progressBar.offsetWidth;
    const newTime = (clickX / width) * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds) || seconds < 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  if (error) {
    return (
      <div className={`audio-player error ${className}`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: '#fee2e2',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        color: '#dc2626',
        fontSize: '14px'
      }}>
        <span>⚠️</span>
        <span>Audio unavailable</span>
        {showTitle && clipName && (
          <span style={{ fontWeight: '500' }}>({clipName})</span>
        )}
      </div>
    );
  }

  if (!fileUrl) {
    return (
      <div className={`audio-player no-url ${className}`} style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        background: '#f3f4f6',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        color: '#6b7280',
        fontSize: '14px'
      }}>
        <span>🎵</span>
        <span>Audio not available</span>
        {showTitle && clipName && (
          <span style={{ fontWeight: '500' }}>({clipName})</span>
        )}
      </div>
    );
  }

  const containerStyle = compact ? {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 8px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '4px',
    fontSize: '12px',
    minWidth: '120px',
    width: '120px'
  } : {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    maxWidth: '400px'
  };

  return (
    <div 
      className={`audio-player ${compact ? 'compact' : ''} ${className}`} 
      style={containerStyle}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
    >
      <audio ref={audioRef} src={fileUrl} preload="metadata" crossOrigin="anonymous" />
      
      {/* Play/Pause Button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          togglePlayPause();
        }}
        disabled={isLoading}
        style={{
          background: 'none',
          border: 'none',
          fontSize: compact ? '16px' : '20px',
          cursor: isLoading ? 'wait' : 'pointer',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: compact ? '24px' : '32px',
          height: compact ? '24px' : '32px'
        }}
        title={isPlaying ? 'Pause' : 'Play'}
      >
        {isLoading ? '⏳' : isPlaying ? '⏸️' : '▶️'}
      </button>

      {/* Title (if shown) */}
      {showTitle && clipName && !compact && (
        <div style={{
          fontWeight: '500',
          color: '#374151',
          fontSize: '14px',
          minWidth: '0',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {clipName}
        </div>
      )}

      {!compact && (
        <>
          {/* Progress Bar */}
          <div style={{ flex: 1, minWidth: '100px' }}>
            <div
              style={{
                width: '100%',
                height: '6px',
                background: '#e5e7eb',
                borderRadius: '3px',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleProgressClick(e);
              }}
            >
              <div
                style={{
                  width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%',
                  height: '100%',
                  background: '#3b82f6',
                  borderRadius: '3px',
                  transition: 'width 0.1s ease'
                }}
              />
            </div>
            
            {/* Time Display */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              color: '#6b7280',
              marginTop: '2px'
            }}>
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Volume Control */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            minWidth: '60px'
          }}>
            <span style={{ fontSize: '12px' }}>🔊</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => {
                e.stopPropagation();
                handleVolumeChange(e);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '50px',
                height: '4px',
                background: '#e5e7eb',
                borderRadius: '2px',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>
        </>
      )}

      {/* Compact mode: just show title and time */}
      {compact && showTitle && clipName && (
        <span style={{
          fontSize: '12px',
          color: '#6b7280',
          maxWidth: '100px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {clipName}
        </span>
      )}

      {compact && (
        <span style={{
          fontSize: '11px',
          color: '#9ca3af',
          fontFamily: 'monospace',
          minWidth: '50px',
          textAlign: 'right'
        }}>
          {duration > 0 && !isNaN(duration) && isFinite(duration) 
            ? `${formatTime(currentTime)}/${formatTime(duration)}`
            : '--:--'
          }
        </span>
      )}
    </div>
  );
};

export default AudioPlayer;